import { geral } from './geral';
import { modais } from './modais';
import { orcamento } from './orcamento';
import { relatorios } from './relatorios';

/**
 * Traduções NOSSAS, por cima do pt-BR oficial do Actual.
 *
 * POR QUE ESTE ARQUIVO EXISTE: o pt-BR do upstream cobre ~73% das frases
 * (1.986 de 2.698 em 05/09/2026), e a pasta `packages/desktop-client/locale/`
 * é um clone do repositório de traduções deles — está no .gitignore e é
 * sobrescrita a cada build. Traduzir lá dentro seria perdido no próximo
 * `git clone`. Aqui é código nosso, versionado, e vence o arquivo deles.
 *
 * REGRA: só entra chave que aparece NO CAMINHO DO DONO — medido, não suposto.
 * Em 06/09/2026 a varredura por `t()`/`<Trans>` achou 592 frases sem tradução;
 * destas, 317 vivem atrás de recursos EXPERIMENTAIS desligados por padrão
 * (`monteCarloReport` e `formulaMode`, em `useFeatureFlag.ts`) e não aparecem
 * na tela. As demais estão traduzidas aqui, divididas por área:
 * `geral.ts`, `orcamento.ts`, `modais.ts` e `relatorios.ts`.
 *
 * ⚠️ Nem tudo o que aparece em inglês é chave faltando. Três categorias fogem
 * do i18n e têm arquivo próprio, com o porquê em cada um:
 *   · nome dos cartões do painel → `nomeDeWidget.ts`
 *   · nome dos temas            → `nomeDeTema.ts`
 *   · categorias do arquivo novo → `categoriasPadrao.ts`
 *
 * ⚠️ `node scripts/conferir-traducoes.mjs` reprova chave que não existe no
 * upstream — o modo de falha aqui é silencioso: um caractere diferente e a
 * frase segue em inglês sem nada acusar.
 *
 * A chave é a frase em inglês, exatamente como está no en.json.
 */
export const traducoesNossas: Record<string, string> = {
  ...geral,
  ...orcamento,
  ...modais,
  ...relatorios,

  // --- Porta de entrada (a primeira tela, antes de existir arquivo) ---
  // A tela de LOGIN do servidor. O upstream traduz para "Entre nesta instância
  // Actual" — o nome deles, na maior frase da primeira tela que o dono vê.
  'Sign in to this Actual instance': 'Entre no FinApp',
  'Welcome to Actual': 'Bem-vindo',
  'Your finances - made simple': 'Seu dinheiro, sem complicação',
  'Actual is a super fast, privacy-focused app for managing your finances. It is 100% free and open source: everything stays on your device, no data is collected, and there is nothing to sign up for.':
    'Tudo fica no seu dispositivo: nenhum dado é enviado pra fora e não existe cadastro. Você começa do zero e o histórico cresce conforme o mês acontece.',
  'Start budgeting': 'Começar',
  // Mantido (não removido): o page model do E2E do upstream depende deste botão.
  // O rótulo honesto já evita a confusão de parecer um jeito de começar de verdade.
  'Try the demo': 'Ver com dados de exemplo',
  'Connect to a sync server': 'Conectar a um servidor',
  'Coming from another budgeting app?': 'Vem de outro aplicativo?',
  'New to budgeting? Take the <2>guided tour</2> to learn how Actual works.':
    'É a primeira vez aqui? Faça o <2>tour guiado</2> pra conhecer as telas.',
  'Using this device only': 'Somente neste dispositivo',
  'Set up sync': 'Configurar sincronização',

  // --- Balão do tour (aparece por cima de todas as telas) ---
  'Welcome to {{appName}}!': 'Bem-vindo!',
  'New to {{appName}}? Take a short tour to learn how budgeting works and find your way around.':
    'Primeira vez por aqui? Faça um tour rápido pra se localizar nas telas.',
  '{{appName}} is a budgeting app that helps you understand exactly where your money goes. This short tour walks you through the basics. It only takes a couple of minutes, and you can leave at any time and replay it later from the Help menu.':
    'Este é o app onde você acompanha pra onde vai o seu dinheiro. O tour mostra o básico em poucos minutos — dá pra sair a qualquer momento e repetir depois pelo menu Ajuda.',
  'Take the tour': 'Fazer o tour',
  'Take a tour': 'Fazer o tour',
  'Take a tour of {{appName}}': 'Fazer o tour do app',
  'Skip tour': 'Agora não',
  'The Help menu is always here when you need it. Use it to replay this tour, browse the documentation, or ask the community on Discord.':
    'O menu Ajuda fica sempre aqui. Use pra repetir este tour ou consultar a documentação.',

  // --- Contas ---
  'You can add more accounts at any time from the sidebar.':
    'Você pode adicionar mais contas quando quiser, pelo menu ao lado.',

  // --- Sincronização (o caminho do celular, Task 17) ---
  'A server syncs your budget across devices and keeps a backup of your data.<1></1>Click to set one up.':
    'Um servidor mantém seus dados iguais em todos os dispositivos e guarda uma cópia de segurança.<1></1>Clique pra configurar.',
  'A sync server keeps your budget up to date across all your devices and unlocks features like bank syncing. It is completely optional: Actual works great on just this device too.':
    'Um servidor mantém tudo atualizado entre seus dispositivos e libera recursos como a conexão com o banco. É opcional: o app funciona bem só neste dispositivo.',
  'Connected to your server — your budget is syncing.':
    'Conectado ao servidor — seus dados estão sincronizando.',
  "Can't reach your server right now.<1></1>Changes are saved locally and will sync once it's reachable again.":
    'Sem conexão com o servidor agora.<1></1>As alterações ficam salvas aqui e sincronizam assim que ele voltar.',
  'Offline — will sync when reconnected':
    'Sem conexão — sincroniza quando voltar',
  'Sync error — click to retry':
    'Erro ao sincronizar — clique pra tentar de novo',
  'Syncing disabled for this file': 'Sincronização desligada neste arquivo',
  'Sync with your server to back up this file and access it on other devices':
    'Sincronize com seu servidor pra ter uma cópia de segurança e abrir em outros dispositivos',
};
